import { ArrowBackOutlined } from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  IconButton,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Button from "../../../components/ui/Button";
import apiService from "../../../service/Api";
import { tokens } from "../../../theme";
import type {
  AddEquipmentFormData,
  IBrandModel,
  IEquipment,
} from "../../../types/equipment";
import AddEquipmentDialog from "./AddEquipmentDialog";
import GearHeader from "./GearHeader";
import LogIssueDialog from "./LogIssue";
import RentOutGearDialog from "./RentGear";
import ScheduleMaintenanceDialog, {
  ScheduleMaintenanceFormData,
} from "./ScheduleMaintenance";
import AllocationsTab from "./tabs/AllocationsTab";
import DocumentsTab from "./tabs/DocumentsTab";
import ImagesTab from "./tabs/ImagesTab";
import MaintenanceTab from "./tabs/MaintenanceTab";
import OverviewTab from "./tabs/OverviewTab";
import RentalsTab from "./tabs/RentalsTab";


export default function ViewEquipmentPage() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const { id } = useParams();
  const navigate = useNavigate();

  const [equipment, setEquipment] = useState<IEquipment | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [logIssueOpen, setLogIssueOpen] = useState(false);
  const [rentOutOpen, setRentOutOpen] = useState(false);
  const [scheduleMaintenanceOpen, setScheduleMaintenanceOpen] = useState(false);
  const [maintenanceRefreshKey, setMaintenanceRefreshKey] = useState(0);
  const [brandModel, setBrandModel] = useState<IBrandModel | null>(null);

  const fetchEquipment = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await apiService.get<IEquipment>(
        `/api/equipments/equipment/${id}/`,
      );
      setEquipment(response.data);
      setBrandModel(response.data.brand_model ?? null);
    } catch (error) {
      console.error("Failed to fetch gear", error);
      setEquipment(null);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) fetchEquipment();
  }, [id, fetchEquipment]);

  const handleUpdateEquipment = async (data: AddEquipmentFormData) => {
    if (!equipment) return;

    try {
      const payload = {
        serial_number: data.unit.serial_number,
        asset_tag: data.unit.asset_tag || undefined,
        status: data.unit.status,
        condition: data.unit.condition,
        location: data.unit.location || undefined,
        description: data.unit.description || undefined,
        purchase_date: data.unit.purchase_date || undefined,
        warranty_expiry_date: data.unit.warranty_expiry_date || undefined,
        purchase_cost: data.unit.purchase_cost || undefined,
        current_value: data.unit.current_value || undefined,
        supplier_name: data.unit.supplier_name || undefined,
        contact_name: data.unit.contact_name || undefined,
        supplier_phone: data.unit.supplier_phone || undefined,
        supplier_email: data.unit.supplier_email || undefined,
        supplier_website: data.unit.supplier_website || undefined,
      };

      await apiService.put(
        `/api/equipments/equipment/${equipment.id}/`,
        payload,
      );

      setEditOpen(false);
      await fetchEquipment();
    } catch (err) {
      console.error("Failed to update equipment", err);
    }
  };

  const handleScheduleMaintenance = async (
    data: ScheduleMaintenanceFormData,
  ) => {
    if (!equipment) return;

    await apiService.post("/api/equipments/maintenance/", {
      equipment: Number(equipment.id),
      maintenance_date: data.maintenanceDate,
      performed_by: data.performedBy,
      notes: data.notes || null,
      cost: data.cost || null,
      next_due_date: data.nextDueDate || null,
      issue: data.issueId ? Number(data.issueId) : null,
    });

    if (data.issueId) {
      await apiService.patch(
        `/api/equipments/equipment-issues/${data.issueId}/`,
        {
          status: "resolved",
        },
      );
    }

    setMaintenanceRefreshKey((k) => k + 1);
    setScheduleMaintenanceOpen(false);
    await fetchEquipment();
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress sx={{ color: colors.primary[400] }} />
      </Box>
    );
  }

  const handleImageSearch = () => {
    const imageUrl =
      equipment?.images?.[0]?.image || equipment?.images?.[0]?.image;

    if (!imageUrl) return;

    const url = `https://lens.google.com/uploadbyurl?url=${encodeURIComponent(
      imageUrl,
    )}`;

    window.open(url, "_blank");
  };

  if (!equipment) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h6">Equipment not found.</Typography>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{ bgcolor: colors.primary[800] }}
        >
          <ArrowBackOutlined />
        </IconButton>
        <Typography
          variant="h5"
          fontWeight="bold"
          color={theme.palette.text.primary}
        >
          Gear Details
        </Typography>
      </Box>

      <GearHeader
        equipment={equipment}
        onImageSearch={handleImageSearch}
        onEdit={() => setEditOpen(true)}
        onReportIssue={() => setLogIssueOpen(true)}
        onRentOut={() => setRentOutOpen(true)}
        brandModel={brandModel}
        onScheduleMaintenance={() => setScheduleMaintenanceOpen(true)}
      />

      <Box
        sx={{
          backgroundColor: colors.primary[900],
          borderRadius: "16px",
          p: 3,
          minHeight: 400,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, newVal) => setActiveTab(newVal)}
          sx={{
            mb: 3,
            minHeight: 36,
            "& .MuiTabs-indicator": {
              backgroundColor: theme.palette.text.primary,
              height: 3,
              borderRadius: "3px 3px 0 0",
            },
          }}
        >
          {[
            "Overview",
            "Allocations",
            "Maintance",
            "Documents",
            "Rentals",
            // "Components",
            "Images",
          ].map((label, i) => (
            <Tab
              key={label}
              label={label}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                color:
                  activeTab === i
                    ? `${theme.palette.text.primary} !important`
                    : theme.palette.text.secondary,
                fontSize: "1rem",
                minWidth: "auto",
                px: 2,
                py: 1,
                minHeight: 36,
              }}
            />
          ))}
        </Tabs>

        <Box
          sx={{
            backgroundColor: colors.primary[800],
            borderRadius: "16px",
            p: 3,
            minHeight: 300,
          }}
        >
          {activeTab === 0 && (
            <OverviewTab
              equipment={equipment}
              brandModel={brandModel}
              onEquipmentUpdated={fetchEquipment}
            />
          )}
          {activeTab === 1 && <AllocationsTab equipment={equipment} />}
          {activeTab === 2 && (
            <MaintenanceTab
              equipment={equipment}
              refreshKey={maintenanceRefreshKey}
            />
          )}
          {activeTab === 3 && <DocumentsTab />}
          {activeTab === 4 && <RentalsTab />}
          {/* {activeTab === 5 && <ComponentsTab equipment={equipment} />} */}
          {activeTab === 5 && (
            <ImagesTab equipment={equipment} onImagesUpdated={fetchEquipment} />
          )}
        </Box>
      </Box>
      <AddEquipmentDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSubmit={handleUpdateEquipment}
        equipment={equipment}
        isEdit
      />

      <LogIssueDialog
        open={logIssueOpen}
        onClose={() => setLogIssueOpen(false)}
        onSubmit={async (data) => {
          await apiService.post(`/api/equipments/equipment-issues/`, {
            equipment: Number(equipment.id),
            issue_type: data.issueType.toLowerCase().replace(" ", "_"),
            description: data.description || null,
            severity: data.severity.toLowerCase(),
            issue_date: data.date || null,
            status: "open",
          });
          setLogIssueOpen(false);
        }}
        equipmentId={equipment.id}
        equipmentName={equipment.gear_name}
      />

      <RentOutGearDialog
        open={rentOutOpen}
        onClose={() => setRentOutOpen(false)}
        onSubmit={async (data) => {
          await apiService.post(`/api/equipments/equipment-rentals/`, {
            equipment: equipment.id,
            client_name: data.clientId,
            rental_start_date: data.startDate,
            rental_end_date: data.endDate,
            daily_rate: data.dailyRate,
          });
          setRentOutOpen(false);
        }}
        equipment={equipment}
        brandModel={brandModel}
      />
      <ScheduleMaintenanceDialog
        open={scheduleMaintenanceOpen}
        onClose={() => setScheduleMaintenanceOpen(false)}
        onSubmit={handleScheduleMaintenance}
        equipment={equipment}
      />
    </Box>
  );
}
